import { TimezoneConstants } from '../constants/timezone-constants';

/**
 * Formats a date string to 'YYYY-MM-DD' format.
 * @param input - The input date string.
 * @returns The formatted date string in 'YYYY-MM-DD' format, or 'Invalid Date' if the input is not a valid date.
 */
export function formatDateToYYYYMMDD(input: string): string {
    if (!input || isNaN(Date.parse(input))) {
        return 'Invalid Date';
    }
    const date = new Date(input);
    return date.toISOString().split('T')[0];
}

/**
 * Formats a Date value to 'YYYY-MM-DD' for DateOnly API query parameters.
 * @param input - The input Date value.
 * @returns The formatted date string in 'YYYY-MM-DD' format, or an empty string if invalid.
 */
export function formatDateToYYYYMMDDFromDate(input: Date): string {
    if (isNaN(input.getTime())) {
        return '';
    }

    const year = input.getFullYear();
    const month = String(input.getMonth() + 1).padStart(2, '0');
    const day = String(input.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
}

/**
 * Formats a date (string or Date object) to 'MM/DD/YYYY' format.
 * Date-only strings in 'YYYY-MM-DD' format are parsed directly from their components to avoid
 * any timezone shift. All other inputs are interpreted as UTC when constructing the Date object,
 * so the returned date reflects the UTC calendar date rather than the local timezone.
 *
 * @param value - The input date, which can be a Date object or a string.
 *                If it's a string, it should be in a format that can be parsed by the Date constructor.
 * @returns The formatted date string in 'MM/DD/YYYY' format, or an empty string if the input is not a valid date.
 */
export function formatDateToMMDDYYYY(
    value: Date | string | null | undefined,
): string {
    if (!value) {
        return '';
    }

    if (typeof value === 'string') {
        const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|T|\s)/);
        if (dateOnlyMatch) {
            const [, year, month, day] = dateOnlyMatch;
            return `${month}/${day}/${year}`;
        }
    }

    const date = new Date(value);
    if (isNaN(date.getTime())) {
        return '';
    }

    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const year = date.getUTCFullYear();

    return `${month}/${day}/${year}`;
}

/**
 * Formats a month number to a two-digit string with leading zero if necessary.
 *
 * @param input - The input month number (1-12).
 * @returns The formatted month string in 'MM' format, or 'Invalid Month' if the input is not a valid month number.
 */
export function formatNumericalMonthToMM(input: number): string {
    if (isNaN(input) || input < 1 || input > 12) {
        return 'Invalid Month';
    }

    return input.toString().padStart(2, '0');
}

/**
 * Formats a date string, replacing '-' with '/' and removing time information. This removes issues with timezones with ISO date strings
 * @param input - The input date string.
 * @returns The formatted date string with '-' replaced with '/' and no time information, or an empty string if the input is not a valid date.
 */
export function parseDateToDateOnly(input: string): string {
    if (!input || isNaN(Date.parse(input))) {
        return '';
    }
    return input.replace(/-/g, '/').replace(/T.+/, '');
}

/**
 * Formats a date (string or Date object) to Eastern Time and returns it in the format 'MM/DD/YY H:MMAM/PM', used in data grids.
 * @param value - UTC ISO-8601 datetime string (timezone assumed UTC if omitted),
 *                or a Date object
 * @returns Formatted string in MM/DD/YY H:MMAM/PM format, or empty string if invalid
 *
 * @example
 * formatUTCToEastern('2025-12-02T15:08:40.4708929') // '12/02/25 10:08AM'
 */
export function parseDateToEasternGridFormat(input: string | Date): string {
    const date = formatToDate(input);
    return date ? formatDateToGridFormat(date) : '';
}

/**
 * Formats a date (string or Date object) to Eastern Time and returns only the time portion in 'H:MM AM/PM EST' format.
 * @param value - UTC ISO-8601 datetime string (timezone assumed UTC if omitted),
 *                or a Date object
 * @returns Formatted string in H:MM AM/PM EST format, or empty string if invalid
 *
 * @example
 * formatUTCToEastern('2025-12-02T15:08:40.4708929') // '10:08 AM EST'
 */
export function parseDateToEasternTimeOnly(input: string | Date): string {
    const date = formatToDate(input);
    return date ? formatDateToEasternTimeOnly(date) : '';
}

/**
 * Formats a date (string or Date object) to Eastern Time and returns only the date portion in 'MM/DD/YY' format.
 * @param value - UTC ISO-8601 datetime string (timezone assumed UTC if omitted),
 *                or a Date object
 * @returns Formatted string in MM/DD/YY format, or empty string if invalid
 *
 * @example
 * formatUTCToEastern('2025-12-02T15:08:40.4708929') // '12/02/25'
 */
export function parseDateToEasternDateOnly(input: string | Date): string {
    const date = formatToDate(input);
    return date ? formatDateToEasternDateOnly(date) : '';
}

/**
 * Formatter used to display all UTC timestamps in the application's
 * standard user-facing timezone (US Eastern / America/New_York).
 *
 * Business rule: all dates returned from the API are UTC and must be
 * rendered consistently in Eastern time, with DST handled automatically.
 */
const easternFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: TimezoneConstants.defaultTimezoneToDisplay,
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
});

/**
 * Converts a UTC date string or Date object to a Date object.
 * Strings without timezone info are treated as UTC.
 *
 * @param value - UTC ISO-8601 datetime string (timezone assumed UTC if omitted),
 *                or a Date object
 * @returns Date object, or null if invalid
 */
export function formatToDate(
    value: Date | string | null | undefined,
): Date | null {
    if (!value) {
        return null;
    }

    let date: Date;
    if (typeof value === 'string') {
        // Check for offset (e.g. +00:00) at the end of the string
        if (/[+-]\d{2}:\d{2}$/.test(value)) {
            date = new Date(value);
        } else {
            const utc = value.endsWith('Z') ? value : value + 'Z';
            date = new Date(utc);
        }
    } else {
        date = value;
    }

    if (isNaN(date.getTime())) {
        return null;
    }

    return date;
}

function formatDateToGridFormat(date: Date): string {
    const parts = easternFormatter.formatToParts(date);
    let month = '',
        day = '',
        year = '',
        hour = '',
        minute = '',
        dayPeriod = '';

    for (const part of parts) {
        switch (part.type) {
            case 'month':
                month = part.value;
                break;
            case 'day':
                day = part.value;
                break;
            case 'year':
                year = part.value;
                break;
            case 'hour':
                hour = part.value;
                break;
            case 'minute':
                minute = part.value;
                break;
            case 'dayPeriod':
                dayPeriod = part.value;
                break;
        }
    }

    return `${month}/${day}/${year} ${hour}:${minute}${dayPeriod}`;
}

function formatDateToEasternTimeOnly(date: Date): string {
    const parts = easternFormatter.formatToParts(date);
    let hour = '',
        minute = '',
        dayPeriod = '',
        timeZoneName = '';
    for (const part of parts) {
        switch (part.type) {
            case 'hour':
                hour = part.value;
                break;
            case 'minute':
                minute = part.value;
                break;
            case 'dayPeriod':
                dayPeriod = part.value;
                break;
            case 'timeZoneName':
                timeZoneName = part.value;
                break;
        }
    }
    return `${hour}:${minute} ${dayPeriod} ${timeZoneName}`;
}

function formatDateToEasternDateOnly(date: Date): string {
    const parts = easternFormatter.formatToParts(date);
    let month = '',
        day = '',
        year = '';
    for (const part of parts) {
        switch (part.type) {
            case 'month':
                month = part.value;
                break;
            case 'day':
                day = part.value;
                break;
            case 'year':
                year = part.value;
                break;
        }
    }
    return `${month}/${day}/${year}`;
}
